import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Container, PageTitle, NbButton, NbCard, Chip } from "@/components/nb";
import { Stagger, Item } from "@/components/motion";
import { CheckCircle2, XCircle, Search, Sparkles, Timer, BookMarked, History, Share2, Zap } from "lucide-react";
import { toast } from "sonner";
import StudyPlan from "@/components/StudyPlan";

const QUESTION_SECONDS = 30;

export default function DiagnosticTest() {
  const { user } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [query, setQuery] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [subject, setSubject] = useState(null); // {id, title}
  const [test, setTest] = useState(null); // {subject, title, questions, ai, aiKey?, bookmarked?}
  const [answers, setAnswers] = useState({}); // idx -> value (int or string)
  const [blankFeedback, setBlankFeedback] = useState({}); // idx -> {correct, hint, checking}
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [times, setTimes] = useState([]); // seconds per Q
  const [current, setCurrent] = useState(0); // active Q index
  const [remaining, setRemaining] = useState(QUESTION_SECONDS);
  const startRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => { api.get("/study/diagnostic/subjects").then((r) => setSubjects(r.data)); }, []);

  // Auto-load a shared bookmark handed off via sessionStorage
  useEffect(() => {
    const raw = sessionStorage.getItem("sl_take_bookmark");
    if (!raw) return;
    try {
      const b = JSON.parse(raw);
      sessionStorage.removeItem("sl_take_bookmark");
      startBookmark(b);
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return subjects;
    return subjects.filter((s) => s.title.toLowerCase().includes(q) || s.id.toLowerCase().includes(q));
  }, [query, subjects]);

  // Timer effect — advances current when time runs out
  useEffect(() => {
    if (!test || result) return;
    startRef.current = Date.now();
    setRemaining(QUESTION_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const left = Math.max(0, QUESTION_SECONDS - elapsed);
      setRemaining(left);
      if (left <= 0) {
        // Record time and auto-advance
        setTimes((t) => { const n = [...t]; n[current] = QUESTION_SECONDS; return n; });
        setCurrent((c) => {
          if (c + 1 < test.questions.length) return c + 1;
          clearInterval(timerRef.current);
          return c;
        });
      }
    }, 200);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [test, current, result]);

  const recordTime = () => {
    const elapsed = (Date.now() - (startRef.current || Date.now())) / 1000;
    setTimes((t) => { const n = [...t]; n[current] = Math.min(QUESTION_SECONDS, elapsed); return n; });
  };

  const goNext = () => {
    recordTime();
    if (current + 1 < test.questions.length) setCurrent(current + 1);
  };
  const goPrev = () => { if (current > 0) setCurrent(current - 1); };

  const start = async (s) => {
    setLoading(true);
    setSubject(s);
    setResult(null);
    setAnswers({});
    setTimes([]);
    setCurrent(0);
    setBlankFeedback({});
    try {
      const r = await api.get(`/study/diagnostic/${s.id}`);
      // ensure `type` present
      const qs = r.data.questions.map((q) => ({ ...q, type: q.type || "mcq" }));
      setTest({ subject: r.data.subject, title: r.data.title, questions: qs, ai: false, aiKey: null });
    } finally { setLoading(false); }
  };

  const generate = async () => {
    const subj = query.trim();
    if (!subj) return toast.error("Type a subject first");
    setGenerating(true);
    setResult(null);
    setAnswers({});
    setTimes([]);
    setCurrent(0);
    setBlankFeedback({});
    try {
      const r = await api.post("/study/diagnostic/generate", { subject: subj, difficulty });
      const aiKey = r.data.questions;
      const clientQs = r.data.questions.map((q) => ({ type: q.type || "mcq", q: q.q, options: q.options, passage: q.passage }));
      setSubject({ id: r.data.subject, title: r.data.title });
      setTest({ subject: r.data.subject, title: r.data.title, questions: clientQs, ai: true, aiKey, difficulty });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate test. Try again.");
    } finally { setGenerating(false); }
  };

  const startBookmark = async (bookmark) => {
    setResult(null);
    setAnswers({});
    setTimes([]);
    setCurrent(0);
    setBlankFeedback({});
    const clientQs = bookmark.questions.map((q) => ({ type: q.type || "mcq", q: q.q, options: q.options, passage: q.passage }));
    setSubject({ id: bookmark.subject, title: bookmark.title });
    setTest({ subject: bookmark.subject, title: bookmark.title, questions: clientQs, ai: true, aiKey: bookmark.questions, difficulty: bookmark.difficulty, bookmarkId: bookmark.id });
  };

  const gradeBlank = async (i) => {
    const q = test.questions[i];
    const stu = answers[i] || "";
    if (!stu.trim()) return toast.error("Type an answer first");
    setBlankFeedback((f) => ({ ...f, [i]: { checking: true } }));
    try {
      const expected = test.ai ? (test.aiKey[i]?.expected || "") : "";
      const r = await api.post("/study/diagnostic/grade-blank", { question: q.q, expected, student_answer: stu });
      setBlankFeedback((f) => ({ ...f, [i]: r.data }));
    } catch {
      setBlankFeedback((f) => ({ ...f, [i]: { correct: false, hint: "Could not check right now." } }));
    }
  };

  const submit = async () => {
    // Ensure last time recorded
    recordTime();
    clearInterval(timerRef.current);
    if (Object.keys(answers).length < test.questions.length) return toast.error("Answer all questions first");
    const list = test.questions.map((q, i) => q.type === "fillblank" ? String(answers[i] ?? "") : (answers[i] ?? -1));
    setLoading(true);
    try {
      const payload = { subject: test.subject, answers: list, time_per_question: test.questions.map((_, i) => times[i] ?? QUESTION_SECONDS) };
      if (test.ai && test.aiKey) {
        payload.ai_questions = test.aiKey;
        payload.ai_title = test.title;
      }
      const r = await api.post("/study/diagnostic/submit", payload);
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const saveBookmark = async () => {
    if (!user) return toast.error("Sign in to bookmark");
    if (!test?.ai || !test?.aiKey) return;
    try {
      await api.post("/study/diagnostic/bookmarks", {
        subject: test.subject, title: test.title, questions: test.aiKey, difficulty: test.difficulty || "medium",
      });
      setTest((t) => ({ ...t, bookmarked: true }));
      toast.success("Bookmarked! Retake it any time.");
    } catch { toast.error("Could not bookmark."); }
  };

  const shareBookmark = async () => {
    if (!user) return toast.error("Sign in to share");
    if (!test?.ai || !test?.aiKey) return;
    try {
      let bid = test.bookmarkId;
      if (!bid) {
        const r = await api.post("/study/diagnostic/bookmarks", {
          subject: test.subject, title: test.title, questions: test.aiKey, difficulty: test.difficulty || "medium",
        });
        bid = r.data.id;
        setTest((t) => ({ ...t, bookmarked: true, bookmarkId: bid }));
      }
      const url = `${window.location.origin}/study/bookmark/${bid}`;
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied!"); }
      catch { toast.success(`Share link: ${url}`); }
    } catch { toast.error("Could not create share link."); }
  };

  const reset = () => { setSubject(null); setTest(null); setResult(null); setAnswers({}); setTimes([]); setCurrent(0); setBlankFeedback({}); };

  const activeQ = test?.questions[current];

  return (
    <Container>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <PageTitle eyebrow="DIAGNOSTIC" title="Where are you right now?" sub="Pick a subject, search any subject, or open a saved test. 5 questions, 30s each." />
        <div className="flex gap-2">
          <Link to="/study/history" data-testid="link-history" className="px-4 py-2 rounded-lg nb-border nb-shadow nb-hover bg-white font-heading font-bold uppercase text-xs flex items-center gap-1"><History className="w-4 h-4" /> History</Link>
          <Link to="/study/bookmarks" data-testid="link-bookmarks" className="px-4 py-2 rounded-lg nb-border nb-shadow nb-hover bg-white font-heading font-bold uppercase text-xs flex items-center gap-1"><BookMarked className="w-4 h-4" /> Saved</Link>
        </div>
      </div>

      {!subject && (
        <>
          <div className="mb-6">
            <div className="flex items-center gap-3 bg-white nb-border nb-shadow rounded-xl px-4 py-3 max-w-2xl">
              <Search className="w-5 h-5 shrink-0" />
              <input
                data-testid="subject-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search a subject (e.g., Sanskrit, French, Astronomy, Law...)"
                className="flex-1 bg-transparent outline-none py-1"
                onKeyDown={(e) => { if (e.key === "Enter") generate(); }}
              />
              {query && (
                <button onClick={() => setQuery("")} data-testid="clear-search" className="font-mono-sl text-[10px] uppercase underline">Clear</button>
              )}
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="font-mono-sl text-[10px] uppercase text-neutral-600">DIFFICULTY (AI):</span>
              {["easy", "medium", "hard"].map((d) => (
                <button key={d} onClick={() => setDifficulty(d)} data-testid={`diff-${d}`} className={`px-3 py-1 rounded-full nb-border font-mono-sl text-[11px] uppercase ${difficulty === d ? "bg-sl-primary nb-shadow" : "bg-white"}`}>{d}</button>
              ))}
              {query && (
                <NbButton onClick={generate} color="black" disabled={generating} className="ml-auto" data-testid="generate-ai-test">
                  <Sparkles className="w-4 h-4 inline-block mr-1" /> {generating ? "Generating..." : `Generate "${query}" test`}
                </NbButton>
              )}
            </div>

            {query && filtered.length === 0 && (
              <NbCard color="tertiary" className="!p-5 mt-4" data-testid="ai-generate-card">
                <Sparkles className="w-6 h-6 mb-2" />
                <div className="font-heading font-black text-xl tracking-tight">No pre-built test for "{query}".</div>
                <div className="text-neutral-800 text-sm">Hit <b>Generate</b> above and AI will craft one at {difficulty} difficulty with a passage + fill-in-the-blank.</div>
              </NbCard>
            )}
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="subject-list">
            {filtered.map((s) => (
              <Item key={s.id}>
                <button onClick={() => start(s)} data-testid={`start-${s.id}`} className="text-left block w-full h-full">
                  <NbCard color="primary" className="nb-hover-lg h-full">
                    <Chip color="tertiary">{s.count} QUESTIONS</Chip>
                    <h3 className="font-heading font-black text-2xl tracking-tighter mt-3">{s.title}</h3>
                    <p className="mt-2 text-neutral-800 text-sm">Tap to begin →</p>
                  </NbCard>
                </button>
              </Item>
            ))}
          </Stagger>
        </>
      )}

      {subject && test && !result && (
        <div>
          <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading font-black text-3xl tracking-tighter">{test.title}</h2>
                {test.ai && <Chip color="accent">AI · {(test.difficulty || "medium").toUpperCase()}</Chip>}
              </div>
              <div className="font-mono-sl text-xs text-neutral-600 mt-1">Question {current + 1} of {test.questions.length}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 nb-border nb-shadow bg-white rounded-lg px-3 py-2" data-testid="timer">
                <Timer className="w-4 h-4" />
                <span className={`font-mono-sl text-sm ${remaining < 8 ? "text-red-600 font-bold animate-pulse" : ""}`}>{Math.ceil(remaining)}s</span>
              </div>
              <button onClick={reset} data-testid="cancel-test" className="font-mono-sl text-xs uppercase underline">Back</button>
            </div>
          </div>

          {/* progress bar */}
          <div className="w-full h-2 bg-white nb-border rounded-full overflow-hidden mb-6">
            <motion.div className="h-full bg-sl-primary" initial={{ width: 0 }} animate={{ width: `${(remaining / QUESTION_SECONDS) * 100}%` }} transition={{ duration: 0.2, ease: "linear" }} />
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={current} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}>
              <NbCard className="!p-6" data-testid={`q-${current}`}>
                {activeQ.passage && (
                  <div className="mb-4 bg-sl-tertiary nb-border rounded-lg p-4">
                    <div className="font-mono-sl text-[10px] uppercase mb-1">PASSAGE</div>
                    <div className="italic text-neutral-800">{activeQ.passage}</div>
                  </div>
                )}
                <div className="flex gap-3">
                  <span className="font-mono-sl text-xs bg-sl-tertiary nb-border rounded-full px-2 py-1 h-fit">Q{current + 1}</span>
                  <div className="flex-1">
                    <div className="font-heading font-bold text-lg">{activeQ.q}</div>

                    {activeQ.type === "fillblank" ? (
                      <div className="mt-4">
                        <input
                          data-testid={`blank-${current}`}
                          value={answers[current] || ""}
                          onChange={(e) => setAnswers({ ...answers, [current]: e.target.value })}
                          placeholder="Type your answer..."
                          className="w-full px-4 py-3 nb-border rounded-lg bg-white"
                        />
                        <div className="mt-3 flex items-center gap-3 flex-wrap">
                          <NbButton onClick={() => gradeBlank(current)} color="tertiary" disabled={blankFeedback[current]?.checking} data-testid={`check-${current}`}>
                            {blankFeedback[current]?.checking ? "Checking..." : "Check with AI"}
                          </NbButton>
                          {blankFeedback[current] && !blankFeedback[current]?.checking && (
                            <div className={`text-sm ${blankFeedback[current]?.correct ? "text-green-700" : "text-red-600"}`} data-testid={`hint-${current}`}>
                              {blankFeedback[current]?.correct ? "✓ Looks good. " : "✗ "}
                              {blankFeedback[current]?.hint}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        {activeQ.options.map((op, oi) => {
                          const active = answers[current] === oi;
                          return (
                            <motion.button
                              key={oi}
                              whileTap={{ scale: 0.97 }}
                              onClick={() => setAnswers({ ...answers, [current]: oi })}
                              data-testid={`q-${current}-opt-${oi}`}
                              className={`text-left px-4 py-3 rounded-lg nb-border font-medium ${active ? "bg-sl-primary nb-shadow" : "bg-white"}`}
                            >
                              {String.fromCharCode(65 + oi)}. {op}
                            </motion.button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </NbCard>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <NbButton onClick={goPrev} color="white" disabled={current === 0} data-testid="prev-q">← Prev</NbButton>
            {current + 1 < test.questions.length ? (
              <NbButton onClick={goNext} color="black" data-testid="next-q">Next →</NbButton>
            ) : (
              <NbButton onClick={submit} color="black" disabled={loading} data-testid="submit-diag">{loading ? "Scoring..." : "See my results"}</NbButton>
            )}
            {test.ai && (
              <div className="ml-auto flex gap-2">
                <NbButton onClick={saveBookmark} color="tertiary" data-testid="bookmark-btn">
                  <BookMarked className="w-4 h-4 inline-block mr-1" /> {test.bookmarked ? "Bookmarked" : "Bookmark"}
                </NbButton>
                <NbButton onClick={shareBookmark} color="accent" data-testid="share-btn">
                  <Share2 className="w-4 h-4 inline-block mr-1" /> Share
                </NbButton>
              </div>
            )}
          </div>

          {/* mini dots for navigation */}
          <div className="mt-6 flex gap-2" data-testid="q-dots">
            {test.questions.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)} data-testid={`dot-${i}`} className={`w-8 h-8 rounded-full nb-border font-mono-sl text-xs ${i === current ? "bg-sl-primary nb-shadow" : (answers[i] !== undefined && answers[i] !== "") ? "bg-sl-tertiary" : "bg-white"}`}>{i + 1}</button>
            ))}
          </div>
        </div>
      )}

      {result && (
        <div data-testid="diag-result">
          <NbCard color="primary" className="!p-8">
            <Chip color="tertiary">RESULT · {(result.title || result.subject).toUpperCase()}</Chip>
            <motion.h2
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 180, damping: 16 }}
              className="font-heading font-black text-5xl md:text-6xl tracking-tighter mt-3">
              {result.pct}<span className="text-3xl">%</span>
            </motion.h2>
            <div className="font-heading text-2xl mt-2">Level: {result.level}</div>
            {result.avg_time_per_q != null && (
              <div className="mt-1 font-mono-sl text-xs uppercase">Avg time per Q: {result.avg_time_per_q}s</div>
            )}
            <p className="mt-3 text-neutral-800 max-w-2xl">{result.recommendation}</p>

            {result.badges?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-3" data-testid="badges">
                {result.badges.map((b) => (
                  <motion.div key={b.id} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }}
                    className="bg-white nb-border nb-shadow rounded-lg px-4 py-2 flex items-center gap-2">
                    <span className="text-2xl">{b.emoji}</span>
                    <div>
                      <div className="font-heading font-black text-sm">{b.title}</div>
                      <div className="font-mono-sl text-[10px] uppercase text-neutral-600">{b.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="mt-6 flex gap-3 flex-wrap">
              <NbButton onClick={reset} color="white" data-testid="try-another">Try another subject</NbButton>
              {test?.ai && (
                <>
                  <NbButton onClick={saveBookmark} color="tertiary" data-testid="save-after">
                    <BookMarked className="w-4 h-4 inline-block mr-1" /> {test.bookmarked ? "Saved" : "Save this test"}
                  </NbButton>
                  <NbButton onClick={shareBookmark} color="accent" data-testid="share-after">
                    <Share2 className="w-4 h-4 inline-block mr-1" /> Share
                  </NbButton>
                </>
              )}
              <a href="/study/chat"><NbButton color="black">Ask Doubt-Buddy</NbButton></a>
            </div>
          </NbCard>

          <h3 className="font-heading font-black text-2xl mt-10 mb-4 tracking-tight">Breakdown</h3>
          <div className="space-y-3">
            {result.breakdown.map((b, i) => (
              <NbCard key={i} className="!p-4 flex items-center gap-3">
                {b.correct ? <CheckCircle2 className="w-5 h-5 text-green-700" /> : <XCircle className="w-5 h-5 text-red-600" />}
                <div className="flex-1">
                  <div className="font-medium">{b.q}</div>
                  <div className="text-sm text-neutral-600">Your answer: {b.your_answer} · Correct: <span className="font-semibold">{b.correct_answer}</span></div>
                </div>
                {b.type === "fillblank" && <Chip color="accent">SHORT ANSWER</Chip>}
              </NbCard>
            ))}
          </div>

          <StudyPlan
            subject={result.title || result.subject}
            level={result.level}
            pct={result.pct}
            weak_topics={result.breakdown.filter((b) => !b.correct).map((b) => b.q).slice(0, 5)}
          />
        </div>
      )}

      {/* expose startBookmark globally so shared page can trigger it via query param, kept simple for now */}
      {/* eslint-disable-next-line */}
      <span style={{ display: "none" }}>{" "}{startBookmark ? "" : ""}</span>
    </Container>
  );
}
